/* eslint-disable tailwindcss/no-custom-classname */
import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import type {
  LinkProps as ChakraLinkProps,
  ThemingProps,
} from '@chakra-ui/react';
import { Link as ChakraLink, Icon } from '@chakra-ui/react';
import type { ComponentProps } from 'react';
import React from 'react';
import type { LinkProps as ReactRouterLinkProps } from 'react-router-dom';
import { Link as ReactRouterLink } from 'react-router-dom';
import { Link as ReactRouterLinkCompat } from 'react-router-dom-v5-compat';

import type { OmitChakraProps } from '../../utils/typeUtils';
import { withChakraAs } from '../../utils/withChakraAs';

type ReactRouterLinkPropsCompat = ComponentProps<typeof ReactRouterLinkCompat>;

export type LinkProps = OmitChakraProps<
  Omit<ChakraLinkProps, 'as' | 'href'>,
  keyof ThemingProps
> &
  (
    | {
        isExternal: true;
        to: string;
        compat?: never;
      }
    | (ReactRouterLinkPropsCompat & {
        isExternal?: false;
        compat?: true;
      })
    | (ReactRouterLinkProps & {
        isExternal?: false;
        compat: false;
      })
  );

/**
 * Orbit Links are meant to be used for branded links.
 *
 * They should not be passed to other Orbit components via the `as` prop.
 * Instead, use React Router's `Link` component or an anchor tag.
 */
export const Link = withChakraAs<LinkProps, 'a'>(
  ({ isExternal, to, children, compat = false, ...rest }) => {
    return isExternal ? (
      <ExternalLink href={to} {...rest}>
        {children}
        <Icon as={IconOutlink} className="orbit-external-link-icon" />
      </ExternalLink>
    ) : compat ? (
      <InternalLinkCompat to={to as ReactRouterLinkPropsCompat['to']} {...rest}>
        {children}
      </InternalLinkCompat>
    ) : (
      <InternalLink to={to} {...rest}>
        {children}
      </InternalLink>
    );
  },
);

type ExternalLinkProps = Omit<ChakraLinkProps, 'isExternal'>;

const ExternalLink = withChakraAs<ExternalLinkProps, 'a'>((props) => {
  return <ChakraLink isExternal {...props} />;
});

const InternalLinkCompat = withChakraAs<ReactRouterLinkPropsCompat, 'a'>(
  (props) => {
    return <ChakraLink as={ReactRouterLinkCompat} {...props} />;
  },
);

const InternalLink = withChakraAs<ReactRouterLinkProps, 'a'>((props) => {
  return <ChakraLink as={ReactRouterLink} {...props} />;
});
